import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { IMAGES, SOCIALS } from "@/content";
import { SocialIcon } from "@/components/SocialIcons";

const MaskLine = ({ index, className = "", children }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ delay: 0.25 + index * 0.14, duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Equalizer = ({ bars = 36, className = "" }) => (
  <div className={`flex items-end gap-[3px] ${className}`} aria-hidden="true">
    {Array.from({ length: bars }).map((_, i) => (
      <span
        key={i}
        className="eq-bar"
        style={{
          animationDelay: `${(i % 14) * 0.08}s`,
          animationDuration: `${0.8 + (i % 5) * 0.14}s`,
          background: ["#CCFF00", "#00F0FF", "#FF0055"][i % 3],
        }}
      />
    ))}
  </div>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 240]);
  const fade = useTransform(scrollY, [0, 650], [1, 0.1]);

  return (
    <section id="home" data-testid="hero-section" className="noise relative min-h-screen flex items-center overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <video
          data-testid="hero-video"
          className="w-full h-[120%] object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={IMAGES.heroPoster}
          aria-hidden="true"
        >
          <source src={IMAGES.heroVideo} type="video/mp4" />
          <source src="/videos/hero-loop.webm" type="video/webm" />
        </video>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/60 to-ink" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(204,255,0,0.12),transparent_45%),radial-gradient(circle_at_85%_70%,rgba(255,0,85,0.12),transparent_45%)]" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-36">
        <MaskLine index={0}>
          <span className="inline-flex items-center gap-3 font-mono text-xs tracking-[0.35em] uppercase text-volt">
            <span className="w-2 h-2 rounded-full bg-volt pulse-dot" /> DJ - Producer - Events - Entertainment
          </span>
        </MaskLine>

        <h1 className="mt-6 font-display font-black uppercase leading-[0.92] tracking-tight text-[clamp(3.2rem,12vw,9.5rem)]">
          <MaskLine index={1}>QT</MaskLine>
          <MaskLine index={2} className="text-neon drop-shadow-[0_0_35px_rgba(204,255,0,0.25)]">2HYPE</MaskLine>
        </h1>

        <div className="mt-8 font-head text-lg sm:text-2xl font-semibold text-zinc-200 uppercase tracking-wide">
          <MaskLine index={3}>Bringing The Energy.</MaskLine>
          <MaskLine index={4} className="text-outline">Creating The Moments.</MaskLine>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <motion.a
            href="#booking"
            data-testid="hero-book-cta"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className="bg-volt text-ink font-head font-bold uppercase tracking-wider px-9 py-4 rounded-full shadow-[0_0_35px_rgba(204,255,0,0.35)] hover:shadow-[0_0_50px_rgba(204,255,0,0.55)] transition-shadow"
          >
            Book QT 2hype
          </motion.a>
          <motion.a
            href="#music"
            data-testid="hero-listen-cta"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className="glass text-white font-head font-bold uppercase tracking-wider px-9 py-4 rounded-full hover:border-cy/60 hover:text-cy transition-colors"
          >
            Listen Now
          </motion.a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-4 sm:left-6 lg:left-8 z-10"
      >
        <Equalizer bars={40} className="h-10" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute right-6 top-1/2 -translate-y-1/2 z-10 hidden lg:flex flex-col gap-5"
      >
        {SOCIALS.map((s) => (
          <a
            key={s.name}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`hero-social-${s.name.toLowerCase()}`}
            aria-label={`QT 2hype on ${s.name}`}
            className="text-zinc-400 hover:text-volt hover:drop-shadow-[0_0_10px_rgba(204,255,0,0.6)] transition-all"
          >
            <SocialIcon name={s.name} className="w-5 h-5" />
          </a>
        ))}
      </motion.div>

      <motion.a
        href="#about"
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-zinc-500 hover:text-volt transition-colors"
        aria-label="Scroll to explore"
      >
        <span className="font-mono text-[10px] tracking-[0.35em] uppercase">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ChevronDown className="w-5 h-5" />
        </motion.span>
      </motion.a>
    </section>
  );
}
